/**
 * The article's right-hand rail — Step 33 rider, taken from the reference's
 * KB editor (captures mined 2026-09-04). Two stacked panels:
 *
 *   Article Properties — location, author, type, keywords
 *   Analytics          — helpful / not helpful / inserted in tickets
 *
 * Views is deliberately absent: the reference shows it, Kai does not want it
 * ("the view counter I don't really care"). The column still exists and
 * still ranks search — it just is not something anyone is asked to read.
 *
 * The rail is shared by the read view and the editor so the two cannot drift;
 * the editor passes `onChange` to make the property fields writable.
 */
import { ARTICLE_TYPES, ARTICLE_TYPE_LABELS, KB_VISIBILITY_LABELS } from './api';
import type { KbArticleDetail, KbArticleType, KbCategory } from './api';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { Select } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { fullTime } from '../../lib/time';

export interface RailDraft {
  folderId: string;
  articleType: KbArticleType;
  keywords: string;
  authorId: string | null;
  /** Step 122 — `yyyy-mm-dd` for the date input, '' for none. */
  reviewOn: string;
}

/** Epoch ms → the date input's `yyyy-mm-dd`, in local time. */
export const toDateInput = (ms: number | null | undefined): string => {
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
/** `yyyy-mm-dd` → epoch ms at local midnight, or null. */
export const fromDateInput = (s: string): number | null => (s ? new Date(`${s}T00:00:00`).getTime() : null);

export function ArticleRail({
  article,
  categories,
  people,
  draft,
  onChange,
}: {
  article: KbArticleDetail;
  categories: KbCategory[];
  /** Staff who can be named as the author. Imported articles have none, so
   *  this is a real field rather than a read-out of who clicked New. */
  people: { id: string; name: string | null; email: string }[];
  /** Present in the editor, absent in the read view — which is what makes
   *  the rail read-only there without a second component. */
  draft?: RailDraft;
  onChange?: (next: RailDraft) => void;
}) {
  const editable = !!draft && !!onChange;
  const value = draft ?? {
    folderId: article.folderId,
    articleType: article.articleType,
    keywords: article.keywords ?? '',
    authorId: article.authorId ?? null,
    reviewOn: toDateInput(article.reviewAt),
  };
  const reviewDue = article.reviewAt != null && article.reviewAt <= Date.now();
  const folders = categories.flatMap((c) => c.folders.map((f) => ({ ...f, categoryName: c.name })));
  const currentFolder = folders.find((f) => f.id === value.folderId);

  return (
    <aside className="w-72 shrink-0 space-y-4">
      <section className="rounded-lg border bg-card p-4">
        <dl className="space-y-2.5 text-xs">
          <Row label="Status">
            {article.status === 'published' ? (
              <Badge variant="outline" className="gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Published
              </Badge>
            ) : (
              <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                Draft
              </Badge>
            )}
          </Row>
          <Row label="Created">{fullTime(article.createdAt)}</Row>
          <Row label="Last modified">{fullTime(article.updatedAt)}</Row>
          {article.publishedAt && <Row label="Published">{fullTime(article.publishedAt)}</Row>}
        </dl>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Article properties
        </h2>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
              Article location
            </label>
            {editable ? (
              <Select
                value={value.folderId}
                onChange={(e) => onChange!({ ...value, folderId: e.target.value })}
                className="h-9 text-xs"
              >
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.categoryName} › {f.name}
                  </option>
                ))}
              </Select>
            ) : (
              <p className="text-sm">{article.folderName}</p>
            )}
            {/* The folder decides who can see this — worth saying on the
                screen where someone moves an article between folders. */}
            {currentFolder && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Visible to {KB_VISIBILITY_LABELS[currentFolder.visibility].label.toLowerCase()}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Author</label>
            {editable ? (
              <Select
                value={value.authorId ?? ''}
                onChange={(e) => onChange!({ ...value, authorId: e.target.value || null })}
                className="h-9 text-xs"
              >
                <option value="">No author</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name ?? p.email}
                  </option>
                ))}
              </Select>
            ) : (
              <p className="text-sm">
                {article.authorName ?? <span className="text-muted-foreground">Not set</span>}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Type</label>
            {editable ? (
              <Select
                value={value.articleType}
                onChange={(e) => onChange!({ ...value, articleType: e.target.value as KbArticleType })}
                className="h-9 text-xs"
              >
                {ARTICLE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ARTICLE_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            ) : (
              <p className="text-sm">{ARTICLE_TYPE_LABELS[article.articleType]}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Review by</label>
            {editable ? (
              <>
                <Input
                  type="date"
                  value={value.reviewOn}
                  onChange={(e) => onChange!({ ...value, reviewOn: e.target.value })}
                  className="h-9 text-xs dark:[color-scheme:dark]"
                  aria-label="Review by"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  When someone should read this again. Publishing without one sets a year from now.
                </p>
              </>
            ) : article.reviewAt ? (
              <p className={cn('text-sm', reviewDue && 'font-medium text-amber-700 dark:text-amber-400')}>
                {new Date(article.reviewAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
                {reviewDue && ' · due for review'}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Not set</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Keywords</label>
            {editable ? (
              <>
                <Input
                  value={value.keywords}
                  onChange={(e) => onChange!({ ...value, keywords: e.target.value })}
                  placeholder="Enter a few keywords or key phrases about the article"
                  className="h-9 text-xs"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Words people search for that the article itself does not say.
                </p>
              </>
            ) : article.keywords ? (
              <div className="flex flex-wrap gap-1">
                {article.keywords
                  .split(',')
                  .map((k) => k.trim())
                  .filter(Boolean)
                  .map((k) => (
                    <Badge key={k} variant="outline" className="text-[11px]">
                      {k}
                    </Badge>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Analytics
        </h2>
        <dl className="space-y-2 text-xs">
          <Row label="Helpful">{article.helpful}</Row>
          <Row label="Not helpful">{article.notHelpful}</Row>
          <Row label="Inserted in tickets">{article.insertedCount}</Row>
        </dl>
      </section>
    </aside>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right">{children}</dd>
    </div>
  );
}
